import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { filter, head, isEmpty } from "lodash";
import playlistStore from "../../zustand/playlist.store";
import { setSchedule } from "../../actions/playlist.actions";
import MobileHeader from "../components/MobileHeader";
import styles from "./MobileScheduleEdit.module.scss";

const DAYS = [
  { id: 0, label: "Sun" }, { id: 1, label: "Mon" }, { id: 2, label: "Tue" },
  { id: 3, label: "Wed" }, { id: 4, label: "Thu" }, { id: 5, label: "Fri" }, { id: 6, label: "Sat" },
];

const MobileScheduleEdit = ({ isNew }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { playlists, updatePlaylist } = playlistStore();

  const playlistId = searchParams.get("playlist") || params.id;

  const playlist = useMemo(() => {
    if (!playlistId || !playlists) return null;
    return head(filter(playlists, (p) => p.id === playlistId));
  }, [playlistId, playlists]);

  const existingSchedule = playlist?.schedule?.[0];

  const [selectedDays, setSelectedDays] = useState([]);
  const [hour, setHour] = useState("09");
  const [minute, setMinute] = useState("00");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (existingSchedule) {
      setSelectedDays(existingSchedule.days || []);
      if (existingSchedule.time) {
        const [h, m] = existingSchedule.time.split(":");
        setHour(h || "09");
        setMinute(m || "00");
      }
    }
  }, [existingSchedule]);

  const toggleDay = (dayId) => {
    setSelectedDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId]
    );
  };

  const handleSave = async () => {
    if (isEmpty(selectedDays) || !playlistId) return;

    const time = `${hour}:${minute}`;
    const schedule = [{ is_shuffled: false, time, days: selectedDays }];

    setIsSaving(true);
    const ok = await setSchedule(playlistId, { schedule });
    setIsSaving(false);

    if (ok) {
      updatePlaylist({ schedule }, playlistId);
      navigate("/mobile/schedule");
    } else {
      alert("Failed to save schedule");
    }
  };

  if (!playlist) {
    return (
      <div className={styles.page}>
        <MobileHeader title="Schedule" showBack />
        <div className={styles.empty}>Playlist not found</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <MobileHeader title={existingSchedule ? "Edit Schedule" : "Add Schedule"} showBack />

      <div className={styles.form}>
        <div className={styles.playlistInfo}>
          <span className={styles.playlistLabel}>Playlist</span>
          <span className={styles.playlistName}>{playlist.name}</span>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Playing Days</label>
          <div className={styles.daysGrid}>
            {DAYS.map((d) => (
              <button
                key={d.id}
                className={`${styles.dayBtn} ${selectedDays.includes(d.id) ? styles.dayActive : ""}`}
                onClick={() => toggleDay(d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Playing Time</label>
          <div className={styles.timeRow}>
            <input
              type="time"
              className={styles.timeInput}
              value={`${hour}:${minute}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(":");
                setHour(h);
                setMinute(m);
              }}
            />
          </div>
        </div>

        {selectedDays.length > 0 && (
          <div className={styles.summary}>
            <span className={styles.summaryLabel}>Summary:</span>
            <span className={styles.summaryText}>
              {selectedDays.sort((a, b) => a - b).map((d) => DAYS.find((day) => day.id === d)?.label).join(", ")} at {hour}:{minute}
            </span>
          </div>
        )}

        <button
          className={styles.saveBtn}
          disabled={isEmpty(selectedDays) || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save Schedule"}
        </button>
      </div>
    </div>
  );
};

export default MobileScheduleEdit;
